<template>
  <div>
    <div v-if="cost.override" class="text-center">
      <h3 class="h3-small fw-bold mb-space-xs">
        {{ costCollectionCaption }}
      </h3>
      <p class="mb-0 fw-bold h2-large">{{ manualCostAmount }}</p>
    </div>

    <div v-else class="text-center">
      <spinner-group v-if="showLoader" class="mb-space-md"></spinner-group>

      <div v-else>
        <div
          class="d-flex flex-column align-items-center justify-content-center mb-space-sm"
        >
          <h3
            class="h3-medium bg-secondary fw-bold py-space-xxxs px-space-xs mb-space-xs"
          >
            {{ calculatorTitle }}
          </h3>
          <div v-if="isGraduate">
            <p class="fs-small mb-space-xxxs">Graduate key:</p>
            <p class="fs-small mb-space-xxxs">9+ credits for full-time</p>
            <p class="fs-small mb-0">
              6 credits for part-time or working students
            </p>
          </div>
          <div v-else>
            <p class="fs-small mb-space-xxxs">Undergraduate key:</p>
            <p class="fs-small mb-space-xxxs">12+ credits for full-time</p>
            <p class="fs-small mb-0">
              6 credits for part-time or working students
            </p>
          </div>
        </div>

        <div
          class="d-flex align-items-center mb-space-sm border border-2 border-light-3 rounded-pill px-space-xs py-space-xxs slider-border-width"
        >
          <h3 class="h3-medium fw-bold me-space-xs">
            {{ sliderValue }}
          </h3>
          <rds-slider
            v-model="sliderValue"
            min="1"
            :max="studentCost?.max_credits"
            aria-label="Select number of credit hours per semester"
            class="flex-fill me-space-xxxs"
            data-cy="tuition-calculator-credits-slider"
          ></rds-slider>
        </div>
        <p class="fs-xs mb-space-sm">
          Drag the slider to see the tuition breakdown by credit hour
        </p>

        <div
          class="mb-space-xs d-flex align-items-start justify-content-start justify-content-md-between flex-column flex-md-row"
        >
          <p class="fs-medium text-start mb-0">Tuition breakdown**</p>
          <div
            class="text-dark-3 mt-space-xs mt-md-0"
            v-if="isSummerTuitionAvailable"
          >
            <input
              class="form-check-input"
              type="checkbox"
              name="include-summer"
              id="include-summer"
              v-model="showSummerTuition"
              @click="handleIncludeSummerCheckboxClick"
            />
            <label for="include-summer" class="fw-bold ms-space-xxs"
              >Include summer</label
            >
          </div>
        </div>
        <collapse-cost-item
          v-for="(data, index) in creditHourCost"
          :key="index"
          :id="generateCollapseId('res', data.id)"
          :title="data.title"
          :amount="data.amount"
          :items="data.items"
          collapse-title-size="small"
          class="mb-space-xxs text-start border-light-4"
          @collapseShown="handleCollapseShown(data.title)"
          @collapseHidden="handleCollapseHidden(data.title)"
          :data-cy="
            index === 0
              ? 'tuition-calculator-expand-button-Fall'
              : 'tuition-calculator-expand-button-spring'
          "
        ></collapse-cost-item>

        <div
          class="bg-dark-3 text-light-1 p-space-xs px-md-space-sm mb-space-sm"
        >
          <div class="d-flex justify-content-between align-items-center">
            <p
              class="fs-medium fw-bold mb-0 text-start"
              data-cy="“tuition-calculator-Academic-year-text”"
            >
              Academic Year Total
            </p>
            <p
              class="fs-medium fw-bold mb-0"
              id="academic-year-total"
              data-cy="tuition-calculator-total-cost"
            >
              {{ academicYearTotal }}
            </p>
          </div>
          <hr class="hr-bg-color my-space-xs mx-0 border border-light-1" />
          <p class="fs-xs mb-0">
            **This is not a bill. This is only an estimate. Special class fees
            are dependent on specific class enrollment and are not included
            here.
          </p>
        </div>
      </div>
    </div>
    <!-- display program url if not a nondegree program -->
    <div v-if="!cost.isNondegree">
      <div
        class="d-flex flex-column align-items-center justify-content-center mb-space-xs"
      >
        <button
          class="btn btn-sm btn-secondary px-space-xs mb-space-xs"
          @click="smoothScrollTo('asuo-rfi-section', true)"
        >
          Request info
        </button>
        <div class="or-decorator">or</div>
      </div>
      <p class="fw-bold mb-0 text-center">
        Learn more about
        <NuxtLink
          external
          :href="cost.detailPage"
          @click.native.prevent="handleProgramClick"
          class="rds-link"
          data-cy="tuition-calculator-program-link"
        >
          {{ cost.programTitle }}
        </NuxtLink>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  AsuTuitionApiCostObject,
  AsuTuitionApiResponse,
  AsuTuitionApiResponseData,
  AsuTuitionApiResponseDataFeeBreakdown,
} from "~/types/types";

import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import CostService from "~/src/services/CostService";

import RdsSlider from "~/components/RdsSlider.vue";
import SpinnerGroup from "~/components/SpinnerGroup.vue";
import CollapseCostItem from "~/components/degree-page/CollapseCostItem.vue";

interface Props {
  cost: AsuTuitionApiCostObject;
  calculatorTitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  calculatorTitle: "Your tuition estimate",
});

const emits = defineEmits<{
  // Emitted when calc error occurs
  (e: "calc-error", value: boolean): void;
}>();

const showError = ref(false);
const showLoader = ref(true);
const isSummerTuitionAvailable = ref<boolean>(false);
const showSummerTuition = ref<boolean>(false);
// default slider to 6 credit hours, per stakeholder request
const sliderValue = ref("6");
const studentCost = ref<AsuTuitionApiResponseData>();

const manualCostAmount = computed(() => {
  return `$${props.cost.amount!.toLocaleString()}`;
});

const academicYearTotal = computed(() => {
  return `$${CostService.calculateAcademicYearTotal(
    studentCost.value!,
    sliderValue.value! as keyof Omit<
      AsuTuitionApiResponseDataFeeBreakdown,
      "descr"
    >,
    showSummerTuition.value
  ).toLocaleString()}`;
});

const costCollectionCaption = computed(() => {
  let caption = "Cost caption";

  if (props.cost.category === "Graduate") {
    caption = "The cost of this program is:";
  } else if (props.cost.category === "Certificates") {
    caption = "The cost of the program per credit hour is:";
  }

  return caption;
});

const isGraduate = computed(() => {
  return props.cost.category === "Graduate" ? true : false;
});

const creditHourCost = computed(() => {
  return CostService.generateResidencyCostData(
    props.cost.academicYear,
    studentCost.value!,
    sliderValue.value! as keyof Omit<
      AsuTuitionApiResponseDataFeeBreakdown,
      "descr"
    >,
    showSummerTuition.value
  );
});

const generateCollapseId = (residency: string, dataId: string) => {
  return `${residency}-${dataId}`;
};

const handleIncludeSummerCheckboxClick = () => {
  let action: string = showSummerTuition.value ? "unclick" : "click";
  analyticsComposable.trackSelectEvent(
    "onclick",
    action,
    "checkbox",
    "main content",
    "your tuition estimate",
    "include summer"
  );
};

const handleCollapseShown = (text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "open",
    "click",
    "main content",
    props.calculatorTitle.toLowerCase(),
    text.toLowerCase(),
    "asu online tuition calculator"
  );
};

const handleCollapseHidden = (text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "close",
    "click",
    "main content",
    props.calculatorTitle.toLowerCase(),
    text.toLowerCase(),
    "asu online tuition calculator"
  );
};

const smoothScrollTo = (targetId: string, inCostCalc = false) => {
  if (targetId == "asuo-rfi-section") {
    analyticsComposable.trackEvent({
      event: "link",
      name: "onclick",
      action: "click",
      type: "internal link",
      region: inCostCalc ? "main content" : "navbar",
      section: inCostCalc
        ? props.calculatorTitle.toLowerCase()
        : "sticky navbar",
      text: "request info",
      component: inCostCalc ? "asu online tuition calculator" : "sticky navbar",
    });

    scrollToTarget(targetId, { offsetElement: "site-header" });
  }
};
const handleProgramClick = (event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    props.calculatorTitle.toLowerCase(),
    props.cost.programTitle ? props.cost.programTitle.toLowerCase() : "",
    "asu online tuition calculator"
  );
  navigateTo(props.cost.detailPage, {
    open: {
      target: "_self",
    },
  });
};

onMounted(async () => {
  try {
    if (!props.cost.override) {
      // initialise resident cost
      await useFetch<AsuTuitionApiResponse>(props.cost.costUrl, {
        server: false,
      }).then((res) => {
        const data = res.data.value?.data;
        const error = res.error.value;
        if (error) {
          showError.value = true;
        } else {
          typeof data == null
            ? (showError.value = true)
            : (studentCost.value = data);
          // set if summer tuition variable should be displayed or not
          if (data !== undefined) {
            isSummerTuitionAvailable.value =
              "FS0005" in data?.breakdown.summer ||
              "FS0008" in data?.breakdown.summer;
          }
        }
        showLoader.value = false;
      });
    } else {
      showLoader.value = false;
    }
  } catch (err) {
    showError.value = true;
    emits("calc-error", showError.value);
  }
});
</script>

<style lang="scss" scoped>
.form-check-input:checked {
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
}
</style>
